import React from 'react';
import { AccountCard, AccountGrid, AccountSection, SectionBadge, StatCard } from './AccountUI';

const mockUsageData = [
    { name: 'Emails', value: 500, color: '#2563eb' },
    { name: 'SMS', value: 300, color: '#60a5fa' },
    { name: 'Voice', value: 150, color: '#93c5fd' },
];

const mockBillingHistory = [
    { id: 1, credits: 500, date: '2024-09-01', amount: '$50' },
    { id: 2, credits: 300, date: '2024-09-15', amount: '$30' },
    { id: 3, credits: 150, date: '2024-09-25', amount: '$15' },
];

const UsageDonutChart = ({ data, size = 168, strokeWidth = 18 }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const totalValue = data.reduce((sum, item) => sum + item.value, 0);
    let accumulatedPercentage = 0;

    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
                    {data.map((item) => {
                        const percentage = (item.value / totalValue) * 100;
                        const dashArray = `${(percentage / 100) * circumference} ${circumference}`;
                        const offset = (accumulatedPercentage / 100) * circumference;
                        accumulatedPercentage += percentage;
                        return (
                            <circle
                                key={item.name}
                                r={radius}
                                cx={size / 2}
                                cy={size / 2}
                                fill="transparent"
                                stroke={item.color}
                                strokeWidth={strokeWidth}
                                strokeDasharray={dashArray}
                                strokeDashoffset={-offset}
                            />
                        );
                    })}
                </g>
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-semibold text-slate-950">{totalValue}</span>
                <span className="text-sm text-slate-400">Used this cycle</span>
            </div>
        </div>
    );
};

export default function BillingAndUsage() {
    const totalUsed = mockUsageData.reduce((sum, item) => sum + item.value, 0);
    const totalCredits = 2000;

    return (
        <div className="p-6 sm:p-8 lg:p-10">
            <AccountSection
                eyebrow="Billing"
                title="A quieter view of credit usage and spend."
                subtitle="Track what has been consumed, what remains, and the purchases that support your workspace activity."
                actions={<SectionBadge tone="blue">Usage cycle active</SectionBadge>}
            >
                <AccountGrid>
                    <div className="xl:col-span-4">
                        <StatCard label="Credits remaining" value={totalCredits - totalUsed} hint="Available to use right now" accent="slate" />
                    </div>
                    <div className="xl:col-span-4">
                        <StatCard label="Used this cycle" value={totalUsed} hint="Combined email, SMS, and voice credits" accent="blue" />
                    </div>
                    <div className="xl:col-span-4">
                        <StatCard label="Latest purchase" value="$50" hint="Most recent invoice amount" accent="emerald" />
                    </div>
                </AccountGrid>

                <AccountGrid>
                    <AccountCard className="xl:col-span-7">
                        <div className="flex flex-col gap-8 lg:flex-row lg:items-center">
                            <UsageDonutChart data={mockUsageData} />
                            <div className="flex-1">
                                <div className="flex items-center justify-between text-sm text-slate-500">
                                    <span>Total allocation</span>
                                    <span className="font-semibold text-slate-950">{totalCredits}</span>
                                </div>
                                <div className="mt-3 h-3 overflow-hidden rounded-full bg-slate-100">
                                    <div className="h-full rounded-full bg-blue-600" style={{ width: `${(totalUsed / totalCredits) * 100}%` }} />
                                </div>

                                <div className="mt-6 space-y-3">
                                    {mockUsageData.map((item) => (
                                        <div key={item.name} className="flex items-center justify-between rounded-2xl border border-slate-200 px-4 py-3">
                                            <div className="flex items-center gap-3">
                                                <span className="h-3 w-3 rounded-full" style={{ backgroundColor: item.color }} />
                                                <span className="text-sm font-medium text-slate-700">{item.name}</span>
                                            </div>
                                            <span className="text-sm font-semibold text-slate-950">{item.value}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </AccountCard>

                    <AccountCard className="xl:col-span-5">
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-slate-950">Top up credits</h3>
                            <p className="text-sm leading-6 text-slate-500">Keep campaigns uninterrupted by adding credits before your team hits a usage bottleneck.</p>
                            <button type="button" className="inline-flex rounded-2xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700">
                                Buy more credits
                            </button>
                        </div>
                    </AccountCard>
                </AccountGrid>

                <AccountCard>
                    <div className="flex items-center justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-slate-950">Billing history</h3>
                            <p className="mt-2 text-sm leading-6 text-slate-500">A simple ledger of recent credit purchases.</p>
                        </div>
                        <SectionBadge tone="slate">{mockBillingHistory.length} invoices</SectionBadge>
                    </div>

                    <div className="mt-6 overflow-x-auto">
                        <table className="min-w-full">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-[0.2em] text-slate-400">
                                    <th className="pb-4 font-semibold">Invoice</th>
                                    <th className="pb-4 font-semibold">Date</th>
                                    <th className="pb-4 font-semibold">Amount</th>
                                    <th className="pb-4 text-right font-semibold">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {mockBillingHistory.map((invoice) => (
                                    <tr key={invoice.id} className="border-b border-slate-100 text-sm">
                                        <td className="py-4 font-medium text-slate-900">{invoice.credits} credits purchase</td>
                                        <td className="py-4 text-slate-500">{invoice.date}</td>
                                        <td className="py-4 text-slate-500">{invoice.amount}</td>
                                        <td className="py-4 text-right">
                                            <button type="button" className="font-semibold text-blue-700 transition hover:text-blue-800">
                                                Download
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </AccountCard>
            </AccountSection>
        </div>
    );
}
